import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { exchangeTokenForSession, loginToTraccar } from '@/lib/traccar-api';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { useBranding } from '@/hooks/useBranding';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useTraccarSession();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  useBranding();

  // Passwordless login via a Traccar token, e.g. a shareable demo link: /login?token=...
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const tokenTried = useRef(false);
  useEffect(() => {
    if (!token || tokenTried.current) return;
    tokenTried.current = true;
    setIsLoading(true);
    exchangeTokenForSession(token)
      .then(async (res) => {
        if (!res.ok) throw new Error('Invalid or expired link');
        await login();
        navigate('/', { replace: true });
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'An error occurred'))
      .finally(() => setIsLoading(false));
  }, [token, login, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await loginToTraccar(identifier.toLowerCase(), password);

      if (result.ok) {
        await login();
        navigate('/');
      } else {
        setError('Invalid email/username or password' );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-300 dark:bg-gray-800">
        <CardHeader className="text-center">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <img className="mx-auto w-full" src={`https://rastreosat.github.io/${window.location.hostname}/logo.png`}
                 alt="{window.location.hostname}"/>
          </div>
          <CardDescription>

          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="identifier">Email ou usuário</Label>
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
